import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa uma business unit
 * 
 * @typedef {Object} BusinessUnit
 * @property {number} id - Identificador único
 * @property {string} nome - Nome da business unit
 * @property {number|null} divisao_id - ID da divisão vinculada
 * @property {Object|null} divisao - Divisão vinculada
 */

/**
 * Representa o payload de criação/atualização de uma business unit
 * 
 * @typedef {Object} BusinessUnitPayload
 * @property {string} nome - Nome da business unit (obrigatório, único por empresa)
 * @property {number} divisao_id - ID da divisão (obrigatório)
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/business-units`,
  item: (empresaId, businessUnitId) => `v1/ativacaoped/${empresaId}/business-units/${businessUnitId}`
};

const LOADING_KEY = 'ativacaoped-business-units';

/**
 * Lista business units com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object|string} [params] - Parâmetros de consulta (objeto) ou query string já montada
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<BusinessUnit>>} Lista paginada de business units
 */
export const getBusinessUnits = (empresaId, params = { page: 1, per_page: 10 }) => {
  // Aceita a query string crua montada pelo MasterDetail (page/per_page/sort/filters).
  const query = typeof params === "string" ? params : parseParams(params);
  let url = resource.base(empresaId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Lista business units simplificadas para componentes de seleção (dropdown, autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @param {number} [params.divisao_id] - Filtro por divisão
 * @returns {Promise<BusinessUnit[]>} Lista de business units
 */
export const getSelecaoBusinessUnits = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Cria uma nova business unit.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {BusinessUnitPayload} data - Dados da business unit a ser criada
 * @returns {Promise<BusinessUnit>} Business unit criada
 */
export const postBusinessUnit = (empresaId, data) => {
  const baseUrl = resource.base(empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Atualiza uma business unit existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessUnitId - ID da business unit a ser atualizada
 * @param {BusinessUnitPayload} data - Dados parciais da business unit para atualização
 * @returns {Promise<BusinessUnit>} Business unit atualizada
 */
export const putBusinessUnit = (empresaId, businessUnitId, data) => {
  const baseUrl = resource.item(empresaId, businessUnitId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui uma business unit.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} businessUnitId - ID da business unit a ser excluída
 * @returns {Promise<void>}
 */
export const deleteBusinessUnit = (empresaId, businessUnitId) => {
  const baseUrl = resource.item(empresaId, businessUnitId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

export default {
  getBusinessUnits,
  getSelecaoBusinessUnits,
  postBusinessUnit,
  putBusinessUnit,
  deleteBusinessUnit
};